import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import { Button, IconButton, InlineNotification } from '@carbon/react';
import routes from '~/routes';
import {
  OpenPanelLeft,
  OpenPanelFilledLeft,
  Download,
  Edit,
  Analytics,
} from '@carbon/react/icons';
import { EmptyState } from 'components/empty-state';
import { analyzeHref } from 'pages/packages/compare/analyze/utils';
import { comparisonAtom, comparisonLoadableAtom } from './atoms';
import { useComparisonParams } from './hooks';
import { ComparisonSummary } from './comparison-summary';
import { ComparisonTotals } from './comparison-totals';
import { ComparisonMetrics } from './comparison-metrics';
import { ComparisonMap } from './comparison-map';
import { ComparisonProperties } from './comparison-properties';
import { ComparisonSidebar, type LayoutSettings } from './comparison-sidebar';
import { ExportComparisonModal } from './export-comparison-modal';
import type { EconomicsPair } from './property-attributes';
import style from './comparison-tool.module.scss';

/**
 * Orchestrates comparison data fetching and conditional rendering.
 * Reads URL params, triggers the comparison POST, and renders
 * loading skeletons or data for each section.
 * @return {JSX.Element} The comparison tool content
 */
export const ComparisonTool = () => {
  const history = useHistory();
  const params = useComparisonParams();
  const triggerComparison = useSetAtom(comparisonAtom);
  const [state] = useAtom(comparisonLoadableAtom);
  // Left settings sidebar visibility, toggled by the header panel button.
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  // Global "Download comparison" export modal (whole comparison, all properties).
  const [exportOpen, setExportOpen] = useState(false);
  // Which main-content sections are shown, driven by the sidebar's Layout Settings.
  const [layout, setLayout] = useState<LayoutSettings>({
    summary: true,
    map: true,
    properties: true,
  });

  useEffect(() => {
    if (params) triggerComparison(params);
  }, [params, triggerComparison]);

  // Both sides' scenario economics, joined per-property into the Properties table,
  // plus the per-slot target pricing map (resolved per property by its own slot).
  // Memoized on the fetch result so a keystroke / filter / color-rule re-render
  // doesn't rebuild it — it's the dependency the downstream row memos key on.
  const economics = useMemo<EconomicsPair | null>(() => {
    const loaded = state.state === 'hasData' ? state.data : null;
    if (!loaded) return null;
    return {
      a: loaded.summary.evaluations.a.economics,
      b: loaded.summary.evaluations.b.economics,
      targetValueBySlot: {
        a: loaded.summary.evaluations.a.targetValueBySlot ?? {},
        b: loaded.summary.evaluations.b.targetValueBySlot ?? {},
      },
      // Per-side eval version, so a property row sources its OpEx/LOE from the per-slot
      // expense attributes (eval-3) rather than the unit-matched economics (eval-2). Whether a
      // slot was stamped is read per-slot in sideEconomics (loePerWell !== null), so no side-level
      // "rates applied" flag is threaded here (INS-12510 review).
      evalVersions: {
        a: loaded.summary.evaluations.a.evalVersion,
        b: loaded.summary.evaluations.b.evalVersion,
      },
    };
  }, [state]);

  if (!params) {
    return (
      <InlineNotification
        kind="error"
        title="Invalid comparison parameters"
        subtitle="Please check the URL contains valid evaluation versions, IDs, and variance threshold."
        lowContrast
      />
    );
  }

  if (state.state === 'hasError') {
    return (
      <InlineNotification
        kind="error"
        title="Comparison failed"
        subtitle={
          state.error instanceof Error
            ? state.error.message
            : 'An unexpected error occurred'
        }
        lowContrast
      />
    );
  }

  const isLoading =
    state.state === 'loading' ||
    (state.state === 'hasData' && state.data === null);
  const data = state.state === 'hasData' ? state.data : null;

  if (!isLoading && data && data.comparisonResults.length === 0) {
    return (
      <EmptyState
        headerText="No comparison results"
        helperText="No slots found for the selected evaluations."
      />
    );
  }

  // The Analyze Value Drivers flow is eval-3-only; the button is hidden for any pair
  // that isn't Eval 3 × Eval 3, so the existing eval-2 comparison is unaffected.
  const canAnalyze =
    params.side_a.eval_version === 'eval_3' &&
    params.side_b.eval_version === 'eval_3';

  return (
    <div className={style.page}>
      <header className={style.header}>
        <h1 className={style.title}>Package Evaluation Comparison</h1>
        <div className={style.headerActions}>
          <IconButton
            kind="tertiary"
            label={
              isSidebarOpen ? 'Hide settings panel' : 'Show settings panel'
            }
            align="bottom"
            onClick={() => setIsSidebarOpen((open) => !open)}
          >
            {isSidebarOpen ? <OpenPanelFilledLeft /> : <OpenPanelLeft />}
          </IconButton>
          <IconButton
            kind="tertiary"
            label="Download comparison"
            align="bottom"
            onClick={() => setExportOpen(true)}
          >
            <Download />
          </IconButton>
          <Button
            kind="primary"
            renderIcon={Edit}
            onClick={() => history.push(routes.compareWizardPath)}
          >
            Edit
          </Button>
          {canAnalyze && (
            <Button
              kind="primary"
              renderIcon={Analytics}
              onClick={() =>
                history.push(
                  analyzeHref(
                    routes.compareAnalyzePath,
                    params.side_a.evaluation_id,
                    params.side_b.evaluation_id
                  )
                )
              }
            >
              Analyze Value Drivers
            </Button>
          )}
        </div>
      </header>
      <div className={style.body}>
        {isSidebarOpen && (
          <aside className={style.sidebar} aria-label="Comparison settings">
            <ComparisonSidebar
              layout={layout}
              onLayoutChange={setLayout}
              results={data?.comparisonResults ?? []}
              economics={economics}
            />
          </aside>
        )}
        <div className={style.main}>
          <div className={style.pageContent}>
            <ComparisonSummary
              evaluations={data?.summary.evaluations ?? null}
              isLoading={isLoading}
            />
            {layout.summary && (
              <>
                <ComparisonTotals
                  summary={data?.summary ?? null}
                  isLoading={isLoading}
                />
                <ComparisonMetrics
                  metrics={data?.metrics ?? null}
                  isLoading={isLoading}
                />
              </>
            )}
            {layout.map && (
              <ComparisonMap
                results={data?.comparisonResults ?? []}
                isLoading={isLoading}
              />
            )}
            {layout.properties && (
              <ComparisonProperties
                results={data?.comparisonResults ?? []}
                isLoading={isLoading}
                economics={economics}
              />
            )}
          </div>
        </div>
      </div>
      <ExportComparisonModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        results={data?.comparisonResults ?? []}
        economics={economics}
        evaluations={data?.summary.evaluations ?? null}
      />
    </div>
  );
};
